import type { Client, Room } from '@colyseus/sdk';
import type { Character, ScenePeer } from '../shared/types';
import type {
  RoomJoinGrant,
  FriendMeetingRequest,
  FriendMeetingReason,
  FriendMeetingResult,
  FriendMeetingTicket,
  HandoffCommitted,
  MergeOffer,
  MergeJoin,
} from '../shared/room-routing';
import { EMOTE_PRESETS, type EmotePreset, type SceneEmote } from '../shared/emotes';

export type { FriendMeetingRequest, FriendMeetingResult } from '../shared/room-routing';
type Status = 'connecting' | 'online' | 'offline' | 'elsewhere' | 'waiting';
export interface RoomSessionState {
  peers: ScenePeer[];
  emotes: Record<string, SceneEmote>;
  status: Status;
  roomId: string;
  newsEpoch: number;
  meetingResult?: FriendMeetingResult;
}
type LegacyTicket = { endpoint: string; token: string };
type MeetResponse = FriendMeetingTicket | { roomId: string; zone: string; location: string };
type RoomRecord = {
  room: Room;
  endpoint: string;
  handoffToken?: string;
  transferId?: string;
  mode: 'reservation' | 'legacy';
  healthy: boolean;
  reconnecting: boolean;
  retired: boolean;
  committed: Map<string, HandoffCommitted>;
  observers: Set<() => void>;
};
export interface RoomSessionDependencies {
  character: () => Character | undefined;
  post: <T>(path: string, body: unknown) => Promise<T>;
  client: (
    endpoint: string,
  ) => Promise<Pick<Client, 'consumeSeatReservation' | 'reconnect' | 'joinOrCreate' | 'auth'>>;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  sessionEnded: (restricted: boolean) => void;
  changed: (state: RoomSessionState) => void;
}

const expires = (value: string | number) => (typeof value === 'number' ? value : Date.parse(value));
const isGrant = (value: RoomJoinGrant | LegacyTicket): value is RoomJoinGrant =>
  !!value && 'reservation' in value && !!value.reservation && typeof value.handoffToken === 'string';
class RoomHandshakeTimeout extends Error {}

// This controller owns network transitions. Its test harness uses real promises,
// room signals and timers, but deliberately does not claim React/browser proof.
export class RoomSession {
  private state: RoomSessionState = { peers: [], emotes: {}, status: 'connecting', roomId: '', newsEpoch: 0 };
  private active?: RoomRecord;
  private disposed = false;
  private terminated = false;
  private pendingCancels = new Set<() => void>();
  private tail: Promise<void> = Promise.resolve();
  private queued = 0;
  private running = false;
  private retry?: ReturnType<typeof setTimeout>;
  private attempts = 0;
  private latestMeeting?: FriendMeetingRequest;
  private records = new Set<RoomRecord>();
  private seenNews = new Set<string>();
  private merge?: {
    offer: MergeOffer;
    source: RoomRecord;
    finish: (value: MergeJoin | null) => void;
    timeout: ReturnType<typeof setTimeout>;
  };
  constructor(
    private readonly actor: Pick<Character, 'id' | 'cityId'>,
    private readonly deps: RoomSessionDependencies,
  ) {}
  snapshot() {
    return this.state;
  }
  private alive() {
    const c = this.deps.character();
    return !this.disposed && !this.terminated && c?.id === this.actor.id && c.cityId === this.actor.cityId;
  }
  private publish(patch: Partial<RoomSessionState>) {
    if (!this.alive()) return;
    this.state = { ...this.state, ...patch };
    this.deps.changed(this.state);
  }
  private enqueue(task: () => Promise<void>) {
    this.queued++;
    this.tail = this.tail
      .catch(() => {})
      .then(async () => {
        this.queued--;
        if (!this.alive()) return;
        this.running = true;
        try {
          await task();
        } finally {
          this.running = false;
        }
      });
    // Every transition catches expected network errors. This final catch keeps an
    // unexpected browser API failure from poisoning all later meeting attempts.
    this.tail = this.tail.catch(() => {
      if (!this.active?.healthy) this.publish({ status: 'offline' });
    });
  }
  start() {
    this.enqueue(() => this.connect());
  }
  meeting(request: FriendMeetingRequest) {
    this.latestMeeting = request;
    this.publish({ meetingResult: undefined });
    if (this.retry) {
      clearTimeout(this.retry);
      this.retry = undefined;
    }
    this.enqueue(async () => {
      if (this.latestMeeting !== request) return;
      await this.meet(request);
    });
  }
  private current(request: FriendMeetingRequest) {
    return this.alive() && this.latestMeeting === request;
  }
  private failMeeting(request: FriendMeetingRequest, reason: FriendMeetingReason, committed = false) {
    if (!this.current(request)) return;
    const source = this.active;
    const base = { characterId: this.actor.id, targetId: request.targetId, attempt: request.attempt };
    this.publish({
      meetingResult:
        source?.healthy && !committed
          ? { ...base, outcome: 'fallback', roomId: source.room.roomId, reason }
          : { ...base, outcome: 'unavailable', roomId: source?.room.roomId || null, reason },
    });
  }
  private reason(error: unknown): FriendMeetingReason {
    const code = String((error as { code?: unknown })?.code || '');
    if (/FULL|CAPACITY/.test(code)) return 'full';
    if (/OFFLINE|UNAVAILABLE/.test(code)) return 'offline';
    if (/MOVED|PRESENCE|STALE|ROOM_SOURCE/.test(code)) return 'moved';
    if (/CANCEL|ROOM_TRANSFER/.test(code) || code === 'FRIEND') return 'cancelled';
    return 'network';
  }
  private authError(error: unknown) {
    const code = (error as { code?: string })?.code;
    if (code !== 'UNAUTHORIZED' && code !== 'ACCOUNT_RESTRICTED' && code !== 'SESSION_REVOKED') return false;
    this.endSession(code === 'ACCOUNT_RESTRICTED');
    return true;
  }
  private endSession(restricted: boolean) {
    if (!this.alive()) return;
    this.publish({ status: 'offline', roomId: '', peers: [], emotes: {} });
    this.terminated = true;
    this.cancelPending();
    this.clearSaved();
    this.active = undefined;
    for (const record of [...this.records]) this.retire(record);
    this.deps.sessionEnded(restricted);
  }
  private clearSaved() {
    try {
      const saved = JSON.parse(this.deps.storage.getItem('ado-reconnection') || 'null');
      if (saved?.actor === this.actor.id && saved.cityId === this.actor.cityId)
        this.deps.storage.removeItem('ado-reconnection');
    } catch {
      if (this.alive()) this.deps.storage.removeItem('ado-reconnection');
    }
  }
  private remember(record: RoomRecord) {
    if (!this.alive() || this.active !== record) return;
    try {
      this.deps.storage.setItem(
        'ado-reconnection',
        JSON.stringify({
          actor: this.actor.id,
          cityId: this.actor.cityId,
          endpoint: record.endpoint,
          token: record.room.reconnectionToken,
          mode: record.mode,
        }),
      );
    } catch {
      /* Storage denial must not disconnect an already joined scene. */
    }
  }
  private has(record: RoomRecord, characterId: string) {
    let found = false;
    record.room.state?.players?.forEach((p: any) => {
      if (p.characterId === characterId) found = true;
    });
    return found;
  }
  private read(record: RoomRecord) {
    if (this.active === record && !record.retired) {
      const peers: ScenePeer[] = [],
        emotes: Record<string, SceneEmote> = {};
      record.room.state?.players?.forEach((p: any) => {
        if (EMOTE_PRESETS.includes(p.emote) && p.emoteUntil > 0)
          emotes[p.characterId] = { preset: p.emote, until: p.emoteUntil };
        if (p.characterId !== this.actor.id)
          peers.push({
            characterId: p.characterId,
            name: p.name,
            shirt: p.shirt,
            skin: p.skin,
            hair: p.hair,
            trousers: p.trousers,
            x: p.x,
            z: p.z,
            location: p.location,
            travel: p.travel,
          });
      });
      this.publish({ peers, emotes });
    }
    for (const observer of record.observers) observer();
  }
  private record(
    room: Room,
    endpoint: string,
    mode: RoomRecord['mode'],
    handoffToken?: string,
    transferId?: string,
  ): RoomRecord {
    const record: RoomRecord = {
      room,
      endpoint,
      mode,
      handoffToken,
      transferId,
      healthy: true,
      reconnecting: false,
      retired: false,
      committed: new Map(),
      observers: new Set(),
    };
    this.records.add(record);
    room.onStateChange(() => this.read(record));
    room.onMessage('welcome', () => {});
    room.onMessage('scene.handoff.credentials', (message: { handoffToken: string; transferId: string }) => {
      if (
        this.active !== record ||
        !this.alive() ||
        record.mode !== 'reservation' ||
        typeof message?.handoffToken !== 'string' ||
        !message.handoffToken ||
        typeof message.transferId !== 'string' ||
        (record.transferId && record.transferId !== message.transferId)
      )
        return;
      record.handoffToken = message.handoffToken;
      record.transferId = message.transferId;
      for (const observer of record.observers) observer();
    });
    room.onMessage('scene.handoff.committed', (message: HandoffCommitted) => {
      if (
        record.retired ||
        !this.alive() ||
        message?.roomId !== room.roomId ||
        typeof message.transferId !== 'string'
      )
        return;
      record.committed.set(message.transferId, message);
      for (const observer of record.observers) observer();
    });
    room.onMessage('scene.merge.offer', (message: MergeOffer) => this.offerMerge(record, message));
    room.onMessage('scene.merge.join', (message: MergeJoin) => {
      if (this.merge?.source === record && this.merge.offer.planId === message?.planId)
        this.merge.finish(message);
    });
    room.onMessage('scene.merge.cancel', (message: { planId: string }) => {
      if (this.merge?.source === record && this.merge.offer.planId === message?.planId)
        this.merge.finish(null);
    });
    room.onMessage('news', (message: { eventId: string }) => {
      if (this.active !== record || !this.alive() || this.seenNews.has(message.eventId)) return;
      if (this.seenNews.size > 200) this.seenNews.clear();
      this.seenNews.add(message.eventId);
      this.publish({ newsEpoch: this.state.newsEpoch + 1 });
    });
    room.onDrop(() => {
      record.healthy = false;
      record.reconnecting = true;
      if (this.active === record) this.publish({ emotes: {}, status: 'connecting' });
    });
    room.onReconnect(() => {
      record.healthy = true;
      record.reconnecting = false;
      if (this.active !== record || !this.alive()) return;
      queueMicrotask(() => this.remember(record));
      this.publish({ status: 'online' });
      this.read(record);
      if (record.mode === 'reservation') room.send('scene.handoff.inspect', {});
    });
    room.onLeave((code) => {
      record.healthy = false;
      record.reconnecting = false;
      for (const observer of record.observers) observer();
      this.records.delete(record);
      if (this.active !== record || record.retired || !this.alive()) return;
      this.active = undefined;
      this.clearSaved();
      this.publish({ peers: [], emotes: {}, roomId: '', status: code === 4401 ? 'elsewhere' : 'offline' });
      if (code === 4404 || code === 4406) this.endSession(code === 4404);
      else if (code !== 4401) this.scheduleReconnect();
    });
    room.onError(() => {
      record.healthy = false;
      if (this.active === record) this.publish({ emotes: {}, status: 'offline' });
    });
    return record;
  }
  private activate(record: RoomRecord) {
    const old = this.active;
    this.active = record;
    this.attempts = 0;
    this.remember(record);
    this.publish({ roomId: record.room.roomId, status: 'online' });
    this.read(record);
    // consumeSeatReservation has completed the server onJoin/commit barrier.
    // Only now can retiring the source be safe.
    if (old && old !== record) this.retire(old);
  }
  private retire(record: RoomRecord) {
    record.retired = true;
    record.healthy = false;
    this.records.delete(record);
    for (const observer of record.observers) observer();
    void record.room.leave().catch(() => {});
  }
  private scheduleReconnect() {
    if (!this.alive() || this.retry || this.active?.reconnecting) return;
    this.retry = setTimeout(
      () => {
        this.retry = undefined;
        this.enqueue(() => this.connect());
      },
      Math.min(30000, 1000 * 2 ** this.attempts++) + Math.random() * 1000,
    );
  }
  private async boundedRoom(pending: Promise<Room>, milliseconds: number): Promise<Room> {
    let expired = false;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let cancel!: () => void;
    const deadline = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        expired = true;
        reject(new RoomHandshakeTimeout('Room handshake timed out'));
      }, milliseconds);
      cancel = () => {
        cancelled = true;
        reject(Object.assign(new Error('Room transition cancelled'), { code: 'ROOM_CANCELLED' }));
      };
    });
    this.pendingCancels.add(cancel);
    // The SDK exposes its Room only after JOIN_ROOM. A late resolution cannot
    // retain an abandoned native seat after this controller has timed out.
    void pending.then(
      (room) => {
        if (expired || cancelled) void room.leave().catch(() => {});
      },
      () => {},
    );
    try {
      return await Promise.race([pending, deadline]);
    } finally {
      clearTimeout(timer!);
      this.pendingCancels.delete(cancel);
    }
  }
  private grantWait(grant: RoomJoinGrant) {
    if (!Number.isFinite(grant.expiresAt) || grant.expiresAt <= 0)
      throw new Error('Invalid room reservation deadline');
    // The server owns the 12-second reservation expiry and checks it at commit.
    // A relative client bound avoids relying on the device's wall-clock setting.
    return 15000;
  }
  private async connect() {
    if (this.active?.healthy || !this.alive()) return;
    this.publish({ status: 'connecting' });
    try {
      let resumed: RoomRecord | undefined;
      try {
        const saved = JSON.parse(this.deps.storage.getItem('ado-reconnection') || 'null');
        if (
          saved?.actor === this.actor.id &&
          saved.cityId === this.actor.cityId &&
          saved.endpoint &&
          saved.token
        ) {
          const client = await this.deps.client(saved.endpoint);
          if (!this.alive()) return;
          const room = await this.boundedRoom(client.reconnect(saved.token), 22000);
          if (!this.alive()) {
            void room.leave();
            return;
          }
          resumed = this.record(
            room,
            saved.endpoint,
            saved.mode === 'reservation' ? 'reservation' : 'legacy',
          );
        }
      } catch (error) {
        if (!this.alive()) return;
        if (this.authError(error)) return;
        this.clearSaved();
      }
      if (!this.alive()) return;
      if (resumed) {
        this.activate(resumed);
        if (resumed.mode === 'reservation') resumed.room.send('scene.handoff.inspect', {});
        return;
      }
      const ticket = await this.deps.post<RoomJoinGrant | LegacyTicket>('/realtime-ticket', {
        zone: this.deps.character()!.data.zone,
        protocol: 2,
      });
      if (!this.alive()) return;
      const client = await this.deps.client(ticket.endpoint);
      if (!this.alive()) return;
      let room: Room,
        mode: RoomRecord['mode'],
        handoffToken: string | undefined,
        transferId: string | undefined;
      if (isGrant(ticket)) {
        room = await this.boundedRoom(
          client.consumeSeatReservation(ticket.reservation),
          this.grantWait(ticket),
        );
        mode = 'reservation';
        handoffToken = ticket.handoffToken;
        transferId = ticket.transferId;
      } else {
        // Bounded compatibility for an actual server rollback. New servers issue
        // reservations; a legacy response never participates in handoff/merge.
        client.auth.token = ticket.token;
        room = await this.boundedRoom(
          client.joinOrCreate('neighborhood', { zone: this.deps.character()!.data.zone }),
          15000,
        );
        mode = 'legacy';
      }
      if (!this.alive()) {
        void room.leave();
        return;
      }
      this.activate(this.record(room, ticket.endpoint, mode, handoffToken, transferId));
    } catch (error) {
      if (!this.alive() || this.authError(error)) return;
      this.publish({
        status: String((error as { code?: unknown })?.code) === 'CAPACITY' ? 'waiting' : 'offline',
      });
      this.scheduleReconnect();
    }
  }
  private waitFor(record: RoomRecord, predicate: () => boolean, timeoutMs: number): Promise<boolean> {
    if (!this.alive() || record.retired || !record.healthy) return Promise.resolve(false);
    if (predicate()) return Promise.resolve(true);
    return new Promise((resolve) => {
      const done = (value: boolean) => {
        clearTimeout(timer);
        record.observers.delete(check);
        resolve(value);
      };
      const check = () => {
        if (!this.alive() || record.retired || !record.healthy) done(false);
        else if (predicate()) done(true);
      };
      const timer = setTimeout(() => done(false), timeoutMs);
      record.observers.add(check);
      check();
    });
  }
  private async consume(grant: RoomJoinGrant) {
    if (!isGrant(grant)) throw new Error('Invalid room reservation');
    const wait = this.grantWait(grant);
    const client = await this.deps.client(grant.endpoint);
    if (!this.alive()) return;
    let room: Room;
    try {
      room = await this.boundedRoom(client.consumeSeatReservation(grant.reservation), wait);
    } catch (error) {
      if (error instanceof RoomHandshakeTimeout && this.alive()) {
        // No JOIN_ROOM acknowledgement is not proof that commit did not occur.
        // Do not describe an uncertain physical source socket as a healthy
        // fallback. Recover through a fresh authoritative join instead.
        const uncertain = this.active;
        this.active = undefined;
        if (uncertain) this.retire(uncertain);
        this.clearSaved();
        this.publish({ status: 'offline', roomId: '', peers: [], emotes: {} });
      }
      throw error;
    }
    if (!this.alive()) {
      void room.leave();
      return;
    }
    const record = this.record(room, grant.endpoint, 'reservation', grant.handoffToken, grant.transferId);
    this.activate(record);
    return record;
  }
  private async meet(request: FriendMeetingRequest) {
    if (this.active?.mode === 'legacy') {
      this.failMeeting(request, 'unsupported');
      return;
    }
    let committed = false;
    try {
      if (this.active?.healthy && !this.active.handoffToken) {
        const source = this.active;
        source.room.send('scene.handoff.inspect', {});
        if (!(await this.waitFor(source, () => !!source.handoffToken, 3000))) {
          this.failMeeting(request, 'network');
          return;
        }
      }
      if (!this.alive()) return;
      const response = await this.deps.post<MeetResponse>('/friends/meet', {
        targetId: request.targetId,
        protocol: 2,
        ...(this.active?.healthy && this.active.handoffToken
          ? { sourceHandoffToken: this.active.handoffToken }
          : {}),
      });
      if (!this.alive()) return;
      if (!('kind' in response)) {
        this.failMeeting(request, 'unsupported');
        return;
      }
      if (response.kind === 'already-together') {
        const active = this.active;
        if (
          response.targetId !== request.targetId ||
          !Number.isFinite(response.checkedAt) ||
          !active?.healthy ||
          active.room.roomId !== response.roomId ||
          !this.has(active, this.actor.id) ||
          !this.has(active, request.targetId)
        ) {
          this.failMeeting(request, 'moved');
          return;
        }
        if (this.current(request))
          this.publish({
            meetingResult: {
              characterId: this.actor.id,
              targetId: request.targetId,
              attempt: request.attempt,
              outcome: 'joined',
              roomId: response.roomId,
              checkedAt: response.checkedAt,
              zone: response.zone,
              location: response.location,
            },
          });
        return;
      }
      const grant = response.grant;
      if (grant.kind !== 'friend' || grant.friend?.targetId !== request.targetId) {
        this.failMeeting(request, 'moved');
        return;
      }
      const record = await this.consume(grant);
      if (!record || !this.alive()) return;
      committed = true;
      if (!grant.transferId) {
        this.failMeeting(request, 'network', true);
        return;
      }
      record.room.send('scene.handoff.confirm', { transferId: grant.transferId });
      await this.waitFor(record, () => record.committed.has(grant.transferId!), 5000);
      const confirmation = record.committed.get(grant.transferId);
      const friend = confirmation?.friend;
      if (
        !confirmation ||
        !friend ||
        friend.targetId !== request.targetId ||
        !Number.isFinite(confirmation.checkedAt)
      ) {
        this.failMeeting(request, 'network', true);
        return;
      }
      if (!friend.present) {
        this.failMeeting(request, friend.reason === 'offline' ? 'offline' : 'moved', true);
        return;
      }
      const visible = await this.waitFor(
        record,
        () => this.has(record, this.actor.id) && this.has(record, request.targetId),
        3000,
      );
      if (!visible || !record.healthy || this.active !== record || !this.has(record, request.targetId)) {
        this.failMeeting(request, 'moved', true);
        return;
      }
      if (this.current(request))
        this.publish({
          meetingResult: {
            characterId: this.actor.id,
            targetId: request.targetId,
            attempt: request.attempt,
            outcome: 'joined',
            roomId: record.room.roomId,
            checkedAt: confirmation.checkedAt,
            zone: friend.zone,
            location: friend.location,
          },
        });
    } catch (error) {
      if (!this.alive()) return;
      if (!this.authError(error)) this.failMeeting(request, this.reason(error), committed);
    } finally {
      if (!this.active?.healthy) this.scheduleReconnect();
    }
  }
  private offerMerge(source: RoomRecord, offer: MergeOffer) {
    if (
      !this.alive() ||
      this.active !== source ||
      !source.healthy ||
      source.mode !== 'reservation' ||
      this.merge ||
      this.running ||
      this.queued ||
      !offer?.planId ||
      expires(offer.expiresAt) <= Date.now()
    ) {
      if (offer?.planId && !source.retired)
        source.room.send('scene.merge.ready', { planId: offer.planId, ready: false });
      return;
    }
    let finish!: (value: MergeJoin | null) => void;
    const decision = new Promise<MergeJoin | null>((resolve) => {
      finish = resolve;
    });
    const timeout = setTimeout(() => finish(null), Math.max(1, expires(offer.expiresAt) - Date.now()));
    this.merge = { offer, source, finish, timeout };
    this.enqueue(async () => {
      try {
        source.room.send('scene.merge.ready', { planId: offer.planId, ready: true });
        const message = await decision;
        if (
          !this.alive() ||
          !message ||
          this.active !== source ||
          !source.healthy ||
          message.grant.kind !== 'merge' ||
          message.grant.reservation.roomId !== offer.targetRoomId
        )
          return;
        await this.consume(message.grant);
      } catch (error) {
        this.authError(error);
        /* A failed merge retains a healthy source; revoked identities terminate. */
      } finally {
        clearTimeout(timeout);
        if (this.merge?.offer.planId === offer.planId) this.merge = undefined;
        if (!this.active?.healthy) this.scheduleReconnect();
      }
    });
  }
  sendEmote(preset: EmotePreset) {
    if (!this.active?.healthy || !EMOTE_PRESETS.includes(preset)) return;
    try {
      this.active.room.send('player.emote', { preset });
    } catch {}
  }
  private cancelPending() {
    if (this.retry) {
      clearTimeout(this.retry);
      this.retry = undefined;
    }
    if (this.merge) {
      clearTimeout(this.merge.timeout);
      this.merge.finish(null);
      this.merge = undefined;
    }
    for (const cancel of [...this.pendingCancels]) cancel();
    this.pendingCancels.clear();
  }
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.cancelPending();
    this.clearSaved();
    for (const record of [...this.records]) this.retire(record);
    this.active = undefined;
  }
}
